/**
 * Push lifecycle hooks that run outside React: the idle resync once a user is
 * authenticated and the device clean-up at a deliberate logout (both called by
 * AuthContext). Tiny on purpose —
 * it is in the main chunk; the push client itself (./pushClient) is loaded on
 * demand with import().
 */

const RESYNC_FLAG = 'kaiyloPushResynced';
export const RESYNC_IDLE_TIMEOUT_MS = 10000;
export const RESYNC_FALLBACK_DELAY_MS = 3000;
export const LOGOUT_PUSH_TIMEOUT_MS = 1500;

const notificationsGranted = () =>
  typeof window !== 'undefined'
  && 'Notification' in window
  && window.Notification.permission === 'granted';

const hasAuthToken = () => {
  try {
    if (window.localStorage.getItem('authToken')) return true;
  } catch {
    // Storage blocked: try sessionStorage below.
  }
  try {
    return Boolean(window.sessionStorage.getItem('authToken'));
  } catch {
    return false;
  }
};

/**
 * Once per (session, user), at idle: re-POST this device's subscription (rotated
 * endpoint after a `pushsubscriptionchange` the service worker could not
 * report, device that stayed subscribed across a session expiry or another
 * account's session — the backend upsert re-assigns it to the CURRENT user —,
 * current locale). Called by AuthContext when a user becomes authenticated.
 * Only when notifications are already granted and an auth token exists (the
 * once-per-session flag is not consumed without one, so a later login in the
 * same tab can still resync) — zero cost for everyone else.
 * @param {() => string} getLocale current UI language (read when it runs)
 * @param {string} [userId] the signed-in user, so a second account on the same
 *   device within one session resyncs too
 * @returns {boolean} true when a resync was scheduled
 */
export function schedulePushResync(getLocale, userId) {
  if (!notificationsGranted() || !hasAuthToken()) return false;
  const marker = userId ? String(userId) : '1';
  try {
    if (window.sessionStorage.getItem(RESYNC_FLAG) === marker) return false;
    window.sessionStorage.setItem(RESYNC_FLAG, marker);
  } catch {
    // Storage blocked (private mode): resync on this load anyway.
  }
  const run = () => {
    import('./pushClient')
      .then((client) => client.resyncPushIfEnabled(getLocale()))
      .catch((err) => {
        console.warn('[push] resync failed', (err && err.message) || err);
      });
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: RESYNC_IDLE_TIMEOUT_MS });
  } else {
    // Safari (iOS + macOS) has no requestIdleCallback.
    window.setTimeout(run, RESYNC_FALLBACK_DELAY_MS);
  }
  return true;
}

/**
 * Unsubscribe this device at a logout the USER chose, so a shared device never
 * gets the previous user's notifications. Never called for automatic sign-outs
 * (expired session, another device's global sign-out, failed refresh): those
 * keep the subscription, and the next login resyncs it to the current user. Runs in parallel with the sign-out (the caller
 * never awaits it). `token` must be read BEFORE logout clears it; without one
 * the device is still unsubscribed locally but no server request is made (the
 * backend drops the row on the next 410). Never rejects, never waits longer
 * than `timeoutMs`. No permission = no subscription possible = nothing loaded.
 */
export function cleanupPushOnLogout(token, { timeoutMs = LOGOUT_PUSH_TIMEOUT_MS } = {}) {
  if (!notificationsGranted()) return Promise.resolve();
  let timer;
  const work = import('./pushClient')
    .then((client) => client.disablePush(token ? { token } : { skipServer: true }))
    .catch((err) => {
      console.warn('[push] logout clean-up failed', (err && err.message) || err);
    });
  const timeout = new Promise((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  return Promise.race([work, timeout]).then(() => {
    clearTimeout(timer);
  });
}
